import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Loader2, ArrowRight, Mic, MicOff } from "lucide-react";

const SUGGEST = ["Quanto faturei em março?", "Compare este mês com o mês passado", "O que já foi feito na placa ABC1234?", "Onde cadastro um kit?"];
const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
const KIND_LABEL = { os: "OS", budget: "orçamento", plate: "placa", customer: "ficha do cliente" };

const norm = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[?!,]+/g, " ").replace(/\s+/g, " ").trim();
const p2 = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const WEEK = { domingo: 0, segunda: 1, terca: 2, quarta: 3, quinta: 4, sexta: 5, sabado: 6 };
const STOP = "para|pra|hoje|amanha|depois|segunda|terca|quarta|quinta|sexta|sabado|domingo|dia|as|no|na|com|fazer|\\d";

export function parseSchedule(text) {
  const s = norm(text);
  if (!/\bagend(a|ar|e|em|ando)\b/.test(s)) { return null; }
  const compact = s.replace(/\b([a-z0-9])\s(?=[a-z0-9]\b)/g, "$1");
  const pm = compact.match(/\b([a-z]{3})\s?-?\s?(\d[a-z0-9]\d{2})\b/);
  const plate = pm ? `${pm[1]}${pm[2]}`.toUpperCase() : null;
  const now = new Date();
  let date = null;
  const addDays = (n) => { const d = new Date(now); d.setDate(d.getDate() + n); return isoOf(d); };
  const wd = s.match(/\b(domingo|segunda|terca|quarta|quinta|sexta|sabado)\b/);
  const dm = s.match(/\bdia (\d{1,2})(?:\/(\d{1,2}))?\b/) || s.match(/\b(\d{1,2})\/(\d{1,2})\b/);
  if (/depois de amanha/.test(s)) { date = addDays(2); } else if (/\bamanha\b/.test(s)) { date = addDays(1); } else if (/\bhoje\b/.test(s)) { date = addDays(0); }
  else if (wd) { date = addDays(((WEEK[wd[1]] - now.getDay() + 7) % 7) || 7); }
  else if (dm) {
    const day = Number(dm[1]); const mon = dm[2] ? Number(dm[2]) - 1 : now.getMonth();
    let d = new Date(now.getFullYear(), mon, day);
    if (d < new Date(now.getFullYear(), now.getMonth(), now.getDate())) { d = dm[2] ? new Date(now.getFullYear() + 1, mon, day) : new Date(now.getFullYear(), mon + 1, day); }
    date = isoOf(d);
  }
  const tm = s.match(/\b(?:as|a partir das)\s+(\d{1,2})(?:\s?[:h]\s?(\d{2}))?/) || s.match(/\b(\d{1,2})\s?(?:h|:)\s?(\d{2})?\b/);
  let time = null;
  if (tm && Number(tm[1]) < 24) { time = `${p2(tm[1])}:${p2(tm[2] || 0)}`; }
  let name = null;
  if (!plate) {
    const nm = s.match(new RegExp(`\\b(?:carro|veiculo|moto|cliente)?\\s*d[oa]s?\\s+(?:(?:cliente|senhor|senhora|seu|dona|sr|sra)\\s+)?([a-z][a-z ]*?)(?=\\s+(?:${STOP})|$)`))
      || s.match(new RegExp(`\\bagend(?:a|ar|e)\\s+(?:o|a)\\s+(?!carro|veiculo|placa)([a-z][a-z ]*?)(?=\\s+(?:${STOP})|$)`));
    name = nm ? nm[1].replace(/\b(placa|carro|veiculo)\b/g, "").trim() : null;
  }
  const rxEnd = (re) => { let e = -1; let m; const g = new RegExp(re.source, "gi"); while ((m = g.exec(text))) { e = Math.max(e, m.index + m[0].length); } return e; };
  const end = Math.max(
    rxEnd(/(?:às|as|a partir das)\s+\d{1,2}(?:\s?[:h]\s?\d{2})?\s*(?:h(?:oras?)?\b)?|\b\d{1,2}\s?(?:h|:)\s?(?:\d{2})?\b/),
    rxEnd(/depois de amanhã|depois de amanha|amanhã|amanha|hoje|(?:segunda|terça|terca|quarta|quinta|sexta)(?:-feira)?|sábado|sabado|domingo|dia \d{1,2}(?:\/\d{1,2})?|\b\d{1,2}\/\d{1,2}\b/),
    pm ? rxEnd(/[A-Za-z]{3}\s?-?\s?\d[A-Za-z0-9]\d{2}/) : -1);
  const service = end >= 0 ? text.slice(end).replace(/^[\s,.-]*(?:(?:para|pra|de|com|fazer|uma|um)\s+)*/i, "").replace(/[?.!]+$/, "").trim() : "";
  return { plate, name, date, time, service };
}

export function parseNav(text) {
  const s = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[?!.,]+/g, " ").replace(/\s+/g, " ").trim();
  if (!/\b(abr[ea]|abrir|mostr[ae]|mostrar|me traz|traz|trazer|traga|busca|buscar|procur[ae]|procurar|exib[ae]|ver|cade|qual)\b/.test(s) && !/^placa/.test(s)) { return null; }
  const compact = s.replace(/\b([a-z0-9])\s(?=[a-z0-9]\b)/g, "$1");
  const pm = compact.match(/\b([a-z]{3})\s?-?\s?(\d[a-z0-9]\d{2})\b/);
  const plate = pm ? `${pm[1]}${pm[2]}`.toUpperCase() : null;
  let kind = null;
  if (/orcamento/.test(s)) { kind = "budget"; } else if (!plate && /\bplacas?\b/.test(s)) { kind = "plate"; } else if (/\b(ficha|cadastro)\b/.test(s) || (!plate && /\bcliente\b/.test(s) && !/\b(os|ordem)\b/.test(s))) { kind = "customer"; } else if (plate || /\b(os|ordem|ordens)\b/.test(s)) { kind = "os"; }
  if (!kind) { return null; }
  let name = null;
  if (!plate) {
    const nm = s.match(/\b(?:d[oa]s?|de)\s+(?:(?:cliente|senhor|senhora|seu|dona|sr|sra)\s+)?([a-z][a-z ]{1,60})$/);
    name = nm ? nm[1].replace(/\b(placa|os|orcamento|cliente)\b/g, "").trim() : null;
    if (!name) { return null; }
  }
  return { kind, name, plate };
}

function Md({ text }) {
  const lines = text.split("\n");
  const out = [];
  let tbl = [];
  const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") ? <b key={i} className="text-zinc-50">{p.slice(2, -2)}</b> : p));
  const flush = () => {
    if (!tbl.length) { return; }
    const rows = tbl.filter((l) => !/^\s*\|?\s*:?-{2,}/.test(l)).map((l) => l.replace(/^\s*\||\|\s*$/g, "").split("|").map((c) => c.trim()));
    out.push(<table key={`t${out.length}`} className="w-full text-xs my-2 border border-zinc-800"><tbody>{rows.map((r, i) => <tr key={i} className={i === 0 ? "bg-zinc-900 font-bold" : "border-t border-zinc-800"}>{r.map((c, j) => <td key={j} className="px-2 py-1">{inline(c)}</td>)}</tr>)}</tbody></table>);
    tbl = [];
  };
  lines.forEach((l, i) => {
    if (l.trim().startsWith("|")) { tbl.push(l); return; }
    flush();
    if (l.trim()) { out.push(<p key={i} className="my-1">{inline(l.replace(/^[-*]\s+/, "• "))}</p>); }
  });
  flush();
  return <div>{out}</div>;
}

export function AskAssistant() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const end = useRef(null);
  useEffect(() => { if (open) { api.get("/ai/assistant/usage").then(({ data }) => setUsage(data)).catch(() => {}); } }, [open]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const navigate = useNavigate();
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const ask = async (text) => {
    const t = (text ?? q).trim();
    if (!t || busy) { return; }
    setQ(""); setMsgs((m) => [...m, { me: true, text: t }]); setBusy(true);
    const nav = parseNav(t);
    const sch = parseSchedule(t);
    try {
      if (sch) {
        if (!sch.plate && !sch.name) { setMsgs((m) => [...m, { text: "Para agendar, diga o nome do cliente ou a placa. Ex.: “agenda a placa ABC1D23 amanhã às 14h troca de óleo”." }]); return; }
        const { data } = await api.get("/ai/assistant/find", { params: { kind: "schedule", ...(sch.name ? { name: sch.name } : {}), ...(sch.plate ? { plate: sch.plate } : {}) } });
        const items = (data.items || []).map((it) => ({ ...it, prefill: { customer: { id: it.customer_id, name: it.customer_name }, plate: it.plate, vehicle_label: it.vehicle_label, date: sch.date || "", time: sch.time || "", service_name: sch.service || "" } }));
        const who = sch.plate || sch.name;
        if (items.length === 1) {
          setMsgs((m) => [...m, { text: `Abrindo novo agendamento para **${items[0].label}**. Confira e clique em Salvar.` }]);
          setTimeout(() => go(items[0]), 500);
        } else {
          setMsgs((m) => [...m, { text: items.length ? `Encontrei ${items.length} opções para "${who}". Escolha para agendar:` : `Não encontrei cliente ou veículo para "${who}".`, list: items }]);
        }
        return;
      }
      if (nav) {
        const { data } = await api.get("/ai/assistant/find", { params: { kind: nav.kind, ...(nav.name ? { name: nav.name } : {}), ...(nav.plate ? { plate: nav.plate } : {}) } });
        const items = data.items || [];
        const who = nav.plate || nav.name;
        if (items.length === 1) {
          setMsgs((m) => [...m, { text: `Abrindo ${KIND_LABEL[nav.kind]}: **${items[0].label}** — ${items[0].sub}`, list: items }]);
          setTimeout(() => { setOpen(false); navigate(items[0].path); }, 600);
        } else {
          setMsgs((m) => [...m, { text: items.length ? `Encontrei ${items.length} resultado(s) para "${who}". Escolha:` : `Não encontrei ${KIND_LABEL[nav.kind]} para "${who}".`, list: items }]);
        }
        return;
      }
      const { data } = await api.post("/ai/assistant", { question: t });
      setMsgs((m) => [...m, { text: data.answer, goto: data.goto, period: data.period }]); setUsage({ used: data.used, limit: data.limit });
    } catch (e) { setMsgs((m) => [...m, { text: formatApiError(e), err: true }]); } finally { setBusy(false); }
  };
  const toggleMic = () => {
    if (listening) { recRef.current?.stop(); return; }
    const rec = new SR();
    rec.lang = "pt-BR"; rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onresult = (e) => { const t = e.results?.[0]?.[0]?.transcript || ""; if (t.trim()) { ask(t); } };
    rec.onerror = (e) => { if (e.error !== "no-speech" && e.error !== "aborted") { setMsgs((m) => [...m, { text: "Não consegui usar o microfone. Verifique a permissão do navegador.", err: true }]); } };
    rec.onend = () => setListening(false);
    recRef.current = rec; setListening(true); rec.start();
  };
  const go = (goto) => { setOpen(false); if (goto.prefill) { navigate("/owner/agendamentos", { state: { prefill: goto.prefill } }); return; } navigate(goto.path); };
  return (
    <>
      <button type="button" data-testid="ask-assistant-btn" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-200 text-xs font-bold uppercase hover:bg-amber-500/20 transition-colors">
        <Sparkles className="h-4 w-4" /> Pergunte ao assistente
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="assistant-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-lg max-h-[90dvh] flex flex-col">
          <DialogHeader><DialogTitle className="font-display uppercase flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-300" /> Assistente da oficina</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 min-h-[12rem] text-sm">
            {!msgs.length && (
              <div className="space-y-2">
                <p className="text-zinc-400">Pergunte sobre faturamento, estoque, mecânicos, clientes… Respondo com os números da sua oficina. Também abro telas: “abre a OS da placa ABC1D23”, “abre o orçamento do João”.</p>
                <div className="flex flex-wrap gap-2">{SUGGEST.map((s) => <button key={s} type="button" data-testid={`assistant-suggest-${s.slice(0, 12)}`} onClick={() => ask(s)} className="text-xs rounded-full border border-zinc-700 px-3 py-1.5 text-zinc-300 hover:bg-zinc-900">{s}</button>)}</div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} data-testid={m.me ? `assistant-q-${i}` : `assistant-a-${i}`} className={`rounded-xl px-3 py-2 ${m.me ? "ml-10 bg-green-brand/15 text-green-100" : m.err ? "mr-10 bg-red-950/40 text-red-200" : "mr-6 bg-zinc-900 text-zinc-200"}`}>
                {m.me ? m.text : <Md text={m.text} />}
                {!m.me && m.period && <div data-testid={`assistant-period-${i}`} className="mt-1 text-[10px] font-mono-caps uppercase tracking-widest text-zinc-500">período: {m.period}</div>}
                {!m.me && m.list?.length > 1 && (
                  <div className="mt-2 space-y-1" data-testid={`assistant-list-${i}`}>{m.list.map((it, k) => (
                    <button key={k} type="button" data-testid={`assistant-list-${i}-${k}`} onClick={() => go(it)} className="w-full text-left rounded-lg border border-zinc-800 px-3 py-2 hover:border-green-brand">
                      <div className="text-sm font-bold text-zinc-100">{it.label}</div><div className="text-[11px] text-zinc-500">{it.sub}</div>
                    </button>
                  ))}</div>
                )}
                {!m.me && m.goto && (
                  <button type="button" data-testid={`assistant-goto-${i}`} onClick={() => go(m.goto)}
                          className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-green-brand/15 border border-green-brand/40 text-green-200 text-xs font-bold uppercase hover:bg-green-brand/25">
                    Ir para {m.goto.label} <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
            {busy && <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> pensando…</div>}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 pt-2 border-t border-zinc-900">
            <input data-testid="assistant-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={listening ? "Ouvindo… fale agora" : "Digite ou fale sua pergunta…"} maxLength={500} className="flex-1 min-w-0 h-11 rounded-xl bg-zinc-900 border border-zinc-800 px-3 text-base text-zinc-100" />
            {SR && <Button type="button" data-testid="assistant-mic" onClick={toggleMic} disabled={busy} title={listening ? "Parar" : "Falar"} className={`h-11 ${listening ? "bg-red-600 hover:bg-red-500 animate-pulse" : "bg-zinc-800 hover:bg-zinc-700"} text-white`}>{listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}</Button>}
            <Button type="submit" data-testid="assistant-send" disabled={busy || !q.trim()} className="h-11 bg-green-brand hover:bg-green-brandDark text-white"><Send className="h-4 w-4" /></Button>
          </form>
          {usage && !usage.needs_shop && <div data-testid="assistant-usage" className="text-[11px] text-zinc-500">{usage.used}/{usage.limit} perguntas neste mês</div>}
        </DialogContent>
      </Dialog>
    </>
  );
}
